import moment from "moment";
import {
  IOrderExchange,
  IOrderExchangeLineItem,
  IOrderLineItem,
  IOrderRefund,
  IOrderRefundLineItem,
  IOrderTaxLine,
  IOrderTransaction,
} from "redux/reducers/models/order";

export function attachLineItemModifiers(lineItems: IOrderLineItem[], modifiers: IOrderLineItem[]): IOrderLineItem[] {
  return lineItems.map(item => {
    const itemModifiers = modifiers.filter(modifier => modifier.parent_id === item.id);
    return {
      ...item,
      modifiers: itemModifiers.length !== 0 ? itemModifiers : null,
    };
  });
}

export function lineItemMerchandiseTotal(item: IOrderLineItem): number {
  const own = Number(item.subtotal_price ?? Number(item.price) * Number(item.quantity));
  const modifiers = (item.modifiers ?? []).reduce(
    (sum, modifier) => (sum + Number(modifier.subtotal_price ?? Number(modifier.price) * Number(modifier.quantity))) as number,
    0,
  );
  return (own + modifiers) as number;
}

export function aggregateLineItemTaxLines(items: IOrderLineItem[]): IOrderTaxLine[] {
  const byKey = new Map<string, IOrderTaxLine>();

  const addLines = (lines?: IOrderTaxLine[]) => {
    (lines ?? []).forEach(taxLine => {
      const key = `${taxLine.title}|${taxLine.rate}`;
      const existing = byKey.get(key);
      if (existing) {
        existing.price = Number(existing.price) + Number(taxLine.price);
      } else {
        byKey.set(key, { title: taxLine.title, rate: taxLine.rate, price: Number(taxLine.price) });
      }
    });
  };

  items.forEach(item => {
    addLines(item.tax_lines);
    (item.modifiers ?? []).forEach(modifier => addLines(modifier.tax_lines as Array<IOrderTaxLine>));
  });

  return Array.from(byKey.values());
}

export function isExchangeTender(transaction: IOrderTransaction, exchange: IOrderExchange): boolean {
  const refundIds = (exchange.refunds ?? []).map(refund => refund.id);
  if (transaction.order_refund_id && refundIds.includes(transaction.order_refund_id)) {
    return true;
  }

  if (exchange.settlement_type !== "collect") {
    return false;
  }

  if (transaction.kind !== "sale" && transaction.kind !== "capture") {
    return false;
  }

  if (!exchange.processed_at || !transaction.processed_at) {
    return false;
  }

  return Math.abs(moment.utc(transaction.processed_at).diff(moment.utc(exchange.processed_at), "seconds")) <= 5;
}

export function originalTenders(
  transactions: IOrderTransaction[] | undefined,
  exchanges: IOrderExchange[] | undefined,
): IOrderTransaction[] {
  const list = transactions ?? [];
  if (!exchanges?.length) {
    return list;
  }
  return list.filter(transaction => !exchanges.some(exchange => isExchangeTender(transaction, exchange)));
}

export function exchangeTenders(
  transactions: IOrderTransaction[] | undefined,
  exchange: IOrderExchange,
): IOrderTransaction[] {
  return (transactions ?? []).filter(transaction => isExchangeTender(transaction, exchange));
}

export function exchangeReturnedItems(exchange: IOrderExchange): IOrderRefundLineItem[] {
  const refundItems = (exchange.refunds ?? [])?.flatMap((refund: IOrderRefund) => refund.line_items ?? []) as Array<Record<string, any>>;
  const returnRows = (exchange.exchange_line_items ?? []).filter(row => row.type === "return");
  if (returnRows.length === 0) {
    return refundItems as Array<IOrderRefundLineItem>;
  }

  return returnRows.map(row => {
    const refundItem = refundItems.find((item: Record<string, any>) => item.order_line_item_id === row.order_line_item_id);
    const lineItem = row.line_item;
    return {
      id: row.id,
      order_refund_id: refundItem?.order_refund_id ?? 0,
      order_line_item_id: row.order_line_item_id,
      product_id: lineItem?.product_id,
      variant_id: lineItem?.variant_id,
      product_title: lineItem?.product_title ?? refundItem?.product_title,
      variant_title: lineItem?.variant_title ?? refundItem?.variant_title,
      quantity: row.quantity,
      subtotal_price: refundItem?.subtotal_price ?? Number(lineItem?.price ?? 0) * Number(row.quantity),
      total_tax: refundItem?.total_tax ?? 0,
      total_price: refundItem?.total_price ?? 0,
    };
  });
}

export function exchangeReplacementItems(exchange: IOrderExchange): IOrderLineItem[] {
  const fromJoin = (exchange.exchange_line_items ?? [])
    .filter(row => row.type === "replacement" && row.line_item)
    .map(row => row.line_item);
  if (fromJoin.length > 0) {
    return merchandiseLineItems(fromJoin);
  }
  return merchandiseLineItems(exchange.line_items);
}

export function replacementLineItemIds(exchanges: IOrderExchange[] | undefined): Set<number> {
  const ids = new Set<number>();
  (exchanges ?? []).forEach(exchange => {
    (exchange.exchange_line_items ?? [])
      .filter((row: IOrderExchangeLineItem) => row.type === "replacement")
      .forEach(row => ids.add(row.order_line_item_id));
    (exchange.line_items ?? []).forEach(item => ids.add(item.id));
  });
  return ids;
}

export function merchandiseLineItems(items: IOrderLineItem[] | undefined): IOrderLineItem[] {
  return (items ?? []).filter(item => item.product_type !== "Modifier");
}

export function returnedQuantity(item: IOrderLineItem): number {
  if (item.refundable_quantity == null) {
    return 0;
  }
  return Math.max(0, Number(item.quantity) - Number(item.refundable_quantity));
}
